import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import analyticsService from '../../services/analytics';

/** The Cup Week Pass package; /buy/:packageCode takes members to checkout and visitors to sign-up first. */
export const CUP_WEEK_PASS_CODE = 'cup_week_pass';

/** The banner comes down at the end of Stakes Day: Sun 8 Nov 2026 00:00 in Melbourne (AEDT, UTC+11). */
export const CUP_WEEK_HERO_ENDS = new Date('2026-11-07T13:00:00Z');

export const cupWeekHeroOn = (now: Date = new Date()): boolean => now < CUP_WEEK_HERO_ENDS;

/** Packages every member holds for free (as PromoPackageSection): having only these still means "not paying". */
const DEFAULT_PACKAGES = ['PROMO_PASS', 'FREE_TRACKS'];

/** The Cup Week artwork, linking to the Cup Week Pass checkout. */
export const CupWeekHero: React.FC<{ placement: 'landing' | 'home' }> = ({ placement }) => (
  <Link
    to={`/buy/${CUP_WEEK_PASS_CODE}`}
    className="mb-6 block overflow-hidden rounded-lg shadow-card transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-racing-green/50"
    onClick={() => analyticsService.trackEvent('cup_week_hero_click', { pageType: 'home', metadata: { placement, packageCode: CUP_WEEK_PASS_CODE } })}
  >
    <picture>
      <source
        type="image/webp"
        srcSet="/cup-week-hero-800.webp 800w, /cup-week-hero-1200.webp 1200w, /cup-week-hero-1983.webp 1983w"
        sizes="(min-width: 1280px) 1216px, 100vw"
      />
      <img
        src="/cup-week-hero-1983.jpg"
        width={1983}
        height={793}
        className="block h-auto w-full"
        fetchPriority="high"
        alt="Melbourne Cup Week: a $10 week pass for every race, every track, every day, with top tips and analysis, same race multis and quaddie options, from Derby Day through the Melbourne Cup and Oaks Day. Get your $10 Cup Week Pass now."
      />
    </picture>
  </Link>
);

/**
 * The signed-in home page's banner slot during Cup Week: the artwork for members who aren't paying
 * (the same rule as the promo package banner), nothing for those who already are.
 */
export const MemberCupWeekHero: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { activeSubscriptions } = useSubscriptionAccess();
  const paying = activeSubscriptions.some((s) => !DEFAULT_PACKAGES.includes(s.package?.packageCode ?? ''));
  if (isAuthenticated && paying) return null;
  return <CupWeekHero placement="home" />;
};
